import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  PLATFORM_ID,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { BottomSheetComponent } from '../../component/bottom-sheet/bottom-sheet.component';
import { VoiceAssistantPanelComponent } from '../../../features/assistant/voice/voice-assistant-panel.component';
import { KnowledgeSetupComponent } from '../../../features/assistant/chat/knowledge-setup/knowledge-setup.component';
import type { AssistantArtifactId } from '../../../features/assistant/voice/assistant-artifacts.config';
import { VOICE_ASSISTANT_MARK_SRC } from '../../../features/assistant/voice/voice-assistant-mark.config';
import { AiAssistantAccessService } from '../../services/ai-assistant-access.service';
import { take } from 'rxjs/operators';
import { AssistantWorkflowEngineService } from '../../../features/assistant/chat/orchestrator/assistant-workflow-engine.service';
import { AssistantConversationStateService } from '../../../features/assistant/chat/orchestrator/assistant-conversation-state.service';
import { AssistantOrchestratorService } from '../../../features/assistant/services/assistant-orchestrator.service';
import { VoiceSynthesisService } from '../../services/voice/voice-synthesis.service';

@Component({
  selector: 'app-voice-assistant-fab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    BottomSheetComponent,
    VoiceAssistantPanelComponent,
    KnowledgeSetupComponent,
  ],
  templateUrl: './voice-assistant-fab.component.html',
  styleUrl: './voice-assistant-fab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoiceAssistantFabComponent {
  readonly voiceAssistantMarkSrc = VOICE_ASSISTANT_MARK_SRC;
  readonly aiAccess = inject(AiAssistantAccessService);
  private readonly workflowEngine = inject(AssistantWorkflowEngineService);
  private readonly orchestrator = inject(AssistantOrchestratorService);
  private readonly tts = inject(VoiceSynthesisService);
  readonly conversationState = inject(AssistantConversationStateService);

  private readonly router = inject(Router);

  readonly sheetOpen = signal(false);
  readonly visible = signal(false);
  readonly embeddedArtifact = signal<AssistantArtifactId | null>(null);

  /** When hybrid is open, full Knowledge Setup form starts collapsed (conversation-first). */
  readonly manualFormExpanded = signal(false);

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
    if (isPlatformBrowser(this.platformId)) {
      this.tts.preloadVoices();
    }
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.updateVisibility());

    this.updateVisibility();

    effect(
      () => {
        const art = this.embeddedArtifact();
        if (art) {
          this.workflowEngine.startSession(art);
        } else if (!this.conversationState.workflowSessionActive()) {
          this.workflowEngine.endSession();
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (!this.conversationState.requestOpenHybridArtifact()) return;
        const requested = this.conversationState.consumeOpenHybridRequest()!;
        if (!this.aiAccess.isAiAssistantEnabled()) {
          this.aiAccess.showPremiumModal();
          return;
        }
        this.sheetOpen.set(true);
        this.embeddedArtifact.set(requested as AssistantArtifactId);
        this.manualFormExpanded.set(false);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (!this.embeddedArtifact()) {
          this.manualFormExpanded.set(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (!this.conversationState.requestOpenAssistantSheetSignal()) return;
        // untracked: toggleSheet() reads/writes sheetOpen and other signals.
        // Without this, those reads register as dependencies of *this* effect,
        // so its own write to sheetOpen re-dirties itself — an infinite
        // open/close loop from a single sheet-open request.
        untracked(() => this.toggleSheet());
      },
      { allowSignalWrites: true }
    );
  }

  toggleManualForm(): void {
    this.manualFormExpanded.update(v => !v);
  }

  toggleSheet(): void {
    const openWhenAllowed = () => {
      if (!this.aiAccess.isAiAssistantEnabled()) {
        this.aiAccess.showPremiumModal();
        return;
      }
      if (this.sheetOpen()) {
        this.teardownAssistantSession();
        return;
      }
      this.sheetOpen.set(true);
      if (!this.conversationState.workflowSessionActive()) {
        this.embeddedArtifact.set(null);
        this.conversationState.resetEducationalSession();
      }
    };

    if (!this.aiAccess.accessHydrated()) {
      this.aiAccess
        .ensureAccessLoaded()
        .pipe(take(1))
        .subscribe(() => openWhenAllowed());
      return;
    }
    openWhenAllowed();
  }

  closeSheet(): void {
    this.teardownAssistantSession();
  }

  /** Stops mic/TTS, aborts in-flight chat, and clears workflow when sheet closes or user leaves dashboard. */
  private teardownAssistantSession(): void {
    this.orchestrator.resetSession();
    this.workflowEngine.endSession();
    this.manualFormExpanded.set(false);
    this.conversationState.clearGeneratedEducationalView();
    this.conversationState.resetEducationalSession();
    this.sheetOpen.set(false);
    this.embeddedArtifact.set(null);
  }

  private shouldTeardownAssistantSession(): boolean {
    return (
      this.sheetOpen() ||
      this.orchestrator.listenSessionActive() ||
      this.conversationState.workflowSessionActive()
    );
  }

  clearEmbeddedResource(): void {
    this.manualFormExpanded.set(false);
    this.conversationState.resetEducationalSession();
    this.embeddedArtifact.set(null);
  }

  private updateVisibility(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.visible.set(false);
      return;
    }
    const path = this.router.url.split('?')[0];
    if (!this.isTeacherDashboardPath(path)) {
      if (this.shouldTeardownAssistantSession()) {
        this.teardownAssistantSession();
      } else {
        this.sheetOpen.set(false);
      }
      this.visible.set(false);
      return;
    }
    const hasSession =
      !!localStorage.getItem('token') && !!localStorage.getItem('user');
    this.visible.set(hasSession);
    if (hasSession) {
      this.aiAccess.ensureAccessLoaded().pipe(take(1)).subscribe();
    }
  }

  /** Teacher shell uses `/teacher/dashboard`; legacy layout used `/dashboard`. */
  private isTeacherDashboardPath(path: string): boolean {
    return path === '/dashboard' || path === '/teacher/dashboard';
  }
}
